import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowDown, 
  RefreshCw, 
  Info, 
  DollarSign, 
  Search, 
  ChevronDown, 
  X, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Package, 
  TrendingUp, 
  TrendingDown,
  ExternalLink
} from 'lucide-react';
import { MarketData, DatabaseUserAsset, DatabaseTransaction } from '../hooks/useDatabase';
import { useDatabase } from '../hooks/useDatabase';

interface SwapCryptoPageProps {
  usdtBalance: number;
  btcBalance: number;
  currentBtcPrice: number;
  onSwap: (fromCurrency: string, toCurrency: string, amount: number) => Promise<boolean>;
  marketData?: MarketData[];
  userAssets?: DatabaseUserAsset[];
  fetchTransactions?: () => Promise<void>;
}

interface CryptoCurrency {
  symbol: string;
  name: string;
  iconUrl: string;
  balance?: number;
  price?: number;
}

interface CoinGeckoToken {
  id: string;
  symbol: string;
  name: string;
  image: string;
  current_price: number;
  market_cap: number;
  market_cap_rank: number;
  price_change_percentage_24h: number;
}

// Define the allowed swap symbols
const ALLOWED_SWAP_SYMBOLS = [
  { symbol: 'BTC', name: 'Bitcoin' },
  { symbol: 'ETH', name: 'Ethereum' },
  { symbol: 'USDT', name: 'Tether' },
  { symbol: 'USDC', name: 'USD Coin' },
  { symbol: 'BNB', name: 'BNB' },
  { symbol: 'SOL', name: 'Solana' },
  { symbol: 'XRP', name: 'XRP' },
  { symbol: 'ADA', name: 'Cardano' },
  { symbol: 'DOGE', name: 'Dogecoin' },
  { symbol: 'AVAX', name: 'Avalanche' },
  { symbol: 'MATIC', name: 'Polygon' },
  { symbol: 'TRX', name: 'TRON' },
  { symbol: 'DOT', name: 'Polkadot' },
  { symbol: 'SHIB', name: 'Shiba Inu' },
  { symbol: 'TON', name: 'Toncoin' },
  { symbol: 'APT', name: 'Aptos' },
  { symbol: 'ARB', name: 'Arbitrum' },
  { symbol: 'OP', name: 'Optimism' },
  { symbol: 'LTC', name: 'Litecoin' },
  { symbol: 'PEPE', name: 'PEPE' }
];

const SwapCryptoPage: React.FC<SwapCryptoPageProps> = ({
  usdtBalance,
  btcBalance,
  currentBtcPrice,
  onSwap,
  marketData = [],
  userAssets = [],
  fetchTransactions
}) => {
  const { coingeckoMarketCapData, transactions } = useDatabase();
  
  // State for swap form
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [fromCurrency, setFromCurrency] = useState<CryptoCurrency>({
    symbol: 'USDT',
    name: 'Tether',
    iconUrl: 'https://assets.coingecko.com/coins/images/325/large/Tether.png',
    balance: usdtBalance,
    price: 1
  });
  const [toCurrency, setToCurrency] = useState<CryptoCurrency>({
    symbol: 'BTC',
    name: 'Bitcoin',
    iconUrl: 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png',
    balance: btcBalance,
    price: currentBtcPrice
  });
  
  // State for token selection
  const [showFromTokens, setShowFromTokens] = useState(false);
  const [showToTokens, setShowToTokens] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableCurrencies, setAvailableCurrencies] = useState<CryptoCurrency[]>([]);
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(true);
  const [coingeckoData, setCoingeckoData] = useState<CoinGeckoToken[]>([]);
  const [isLoadingCoingecko, setIsLoadingCoingecko] = useState(true);
  
  // State for swap status
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapError, setSwapError] = useState<string | null>(null);
  const [swapSuccess, setSwapSuccess] = useState<string | null>(null);
  
  // State for confirmation modal
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // State for order history
  const [activeSubTab, setActiveSubTab] = useState<'markets' | 'my_orders'>('markets');
  
  // Refs for dropdown handling
  const fromDropdownRef = useRef<HTMLDivElement>(null);
  const toDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch CoinGecko data
  useEffect(() => {
    const fetchCoingeckoData = async () => {
      try {
        setIsLoadingCoingecko(true);
        console.log('Fetching CoinGecko data via proxy-api...');
        
        // Use the proxy-api Edge Function to avoid CORS issues
        const supabaseFunctionsUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-api`;
        
        const response = await fetch(supabaseFunctionsUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            url: 'https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&per_page=250&page=1',
            method: 'GET'
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch CoinGecko data: ${response.status}`);
        }

        const data = await response.json();
        console.log(`Successfully fetched ${data.length} tokens from CoinGecko`);
        setCoingeckoData(data);
      } catch (error) {
        console.error('Error fetching CoinGecko data:', error);
        // Use fallback data
        setCoingeckoData([
          {
            id: 'bitcoin',
            symbol: 'btc',
            name: 'Bitcoin',
            image: 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png',
            current_price: currentBtcPrice,
            market_cap: 1000000000000,
            market_cap_rank: 1,
            price_change_percentage_24h: 2.5
          },
          {
            id: 'tether',
            symbol: 'usdt',
            name: 'Tether',
            image: 'https://assets.coingecko.com/coins/images/325/large/Tether.png',
            current_price: 1,
            market_cap: 100000000000,
            market_cap_rank: 3,
            price_change_percentage_24h: 0.01
          }
        ]);
      } finally {
        setIsLoadingCoingecko(false);
      }
    };

    fetchCoingeckoData();
  }, [currentBtcPrice]);

  // Update currency balances when they change
  useEffect(() => {
    setFromCurrency(prev => ({
      ...prev,
      balance: prev.symbol === 'USDT' ? usdtBalance : prev.symbol === 'BTC' ? btcBalance : prev.balance
    }));
    
    setToCurrency(prev => ({
      ...prev,
      balance: prev.symbol === 'USDT' ? usdtBalance : prev.symbol === 'BTC' ? btcBalance : prev.balance
    }));
    
    // Also update in available currencies
    setAvailableCurrencies(prev => 
      prev.map(currency => {
        // First check if the currency exists in userAssets
        const userAsset = userAssets.find(asset => asset.asset_symbol === currency.symbol);
        if (userAsset) {
          return {
            ...currency,
            balance: userAsset.balance,
            price: currency.symbol === 'BTC' ? currentBtcPrice :
                   currency.symbol === 'USDT' ? 1 :
                   currency.price
          };
        }
        
        // Fall back to balances for USDT and BTC
        return {
          ...currency,
          balance: currency.symbol === 'USDT' ? usdtBalance : 
                   currency.symbol === 'BTC' ? btcBalance : 
                   currency.balance,
          price: currency.symbol === 'BTC' ? currentBtcPrice :
                 currency.symbol === 'USDT' ? 1 :
                 currency.price
        };
      })
    );
  }, [usdtBalance, btcBalance, currentBtcPrice, userAssets]);

  // Initialize available currencies
  useEffect(() => {
    setIsLoadingCurrencies(true);
    
    // Create initial currencies array based on ALLOWED_SWAP_SYMBOLS
    const initialCurrencies: CryptoCurrency[] = [];
    
    // Process each allowed symbol
    for (const allowedCrypto of ALLOWED_SWAP_SYMBOLS) {
      const symbol = allowedCrypto.symbol;
      const name = allowedCrypto.name;
      
      // Find matching token in CoinGecko data for icon URL
      const geckoToken = coingeckoData.find(t => t.symbol.toLowerCase() === symbol.toLowerCase());
      const iconUrl = geckoToken?.image || `https://assets.coingecko.com/coins/images/missing-token.png`;
      
      // Find price from market data
      let price = 0;
      if (symbol === 'BTC') {
        price = currentBtcPrice;
      } else if (symbol === 'USDT' || symbol === 'USDC') {
        price = 1; // Stablecoins
      } else {
        const marketDataEntry = marketData.find(data => 
          data.symbol === `${symbol}USDT` && 
          data.price > 0 && 
          data.price !== 104325.3716 // Filter out placeholder price
        );
        if (marketDataEntry) {
          price = marketDataEntry.price;
        }
      }
      
      // Get balance from user assets or default balances
      let balance;
      const userAsset = userAssets.find(asset => asset.asset_symbol === symbol);
      if (userAsset) {
        balance = userAsset.balance;
      } else if (symbol === 'BTC') {
        balance = btcBalance;
      } else if (symbol === 'USDT') {
        balance = usdtBalance;
      }
      
      // Add to available currencies
      initialCurrencies.push({
        symbol,
        name,
        iconUrl,
        price,
        balance
      });
    }
    
    setAvailableCurrencies(initialCurrencies);
    setIsLoadingCurrencies(false);
  }, [marketData, usdtBalance, btcBalance, currentBtcPrice, coingeckoData, userAssets]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(event.target as Node)) {
        setShowFromTokens(false);
      }
      if (toDropdownRef.current && !toDropdownRef.current.contains(event.target as Node)) {
        setShowToTokens(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clear error/success messages after 5 seconds
  useEffect(() => {
    if (swapError || swapSuccess) {
      const timer = setTimeout(() => {
        setSwapError(null);
        setSwapSuccess(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [swapError, swapSuccess]);

  // Calculate exchange rate
  const getExchangeRate = () => {
    if (fromCurrency.price && toCurrency.price) {
      return fromCurrency.price / toCurrency.price;
    }
    return 0;
  };

  // Handle from amount change
  const handleFromAmountChange = (value: string) => {
    setFromAmount(value);
    
    // Calculate to amount based on exchange rate
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && fromCurrency.price && toCurrency.price) {
      const exchangeRate = fromCurrency.price / toCurrency.price;
      const calculatedToAmount = numValue * exchangeRate;
      setToAmount(calculatedToAmount.toFixed(8));
    } else {
      setToAmount('');
    }
  };

  // Handle to amount change
  const handleToAmountChange = (value: string) => {
    setToAmount(value);
    
    // Calculate from amount based on exchange rate
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && fromCurrency.price && toCurrency.price) {
      const exchangeRate = toCurrency.price / fromCurrency.price;
      const calculatedFromAmount = numValue * exchangeRate;
      setFromAmount(calculatedFromAmount.toFixed(8));
    } else {
      setFromAmount('');
    }
  };

  // Swap the currencies
  const handleSwapCurrencies = () => {
    const tempCurrency = fromCurrency;
    setFromCurrency(toCurrency);
    setToCurrency(tempCurrency);
    
    // Also swap the amounts
    const tempAmount = fromAmount;
    setFromAmount(toAmount);
    setToAmount(tempAmount);
  };

  // Select a currency for the "from" field
  const handleSelectFromCurrency = (currency: CryptoCurrency) => {
    if (currency.symbol === toCurrency.symbol) {
      // If selecting the same currency as "to", swap them
      setFromCurrency(toCurrency);
      setToCurrency(currency);
    } else {
      setFromCurrency(currency);
    }
    
    setShowFromTokens(false);
    setSearchTerm('');
    
    // Recalculate amounts
    if (fromAmount) {
      handleFromAmountChange(fromAmount);
    }
  };

  // Select a currency for the "to" field
  const handleSelectToCurrency = (currency: CryptoCurrency) => {
    if (currency.symbol === fromCurrency.symbol) {
      // If selecting the same currency as "from", swap them
      setToCurrency(fromCurrency);
      setFromCurrency(currency);
    } else {
      setToCurrency(currency);
    }
    
    setShowToTokens(false);
    setSearchTerm('');
    
    // Recalculate amounts
    if (toAmount) {
      handleToAmountChange(toAmount);
    }
  };

  // Filter tokens based on search term
  const getFilteredTokens = (term: string) => {
    if (!term) return availableCurrencies;
    
    const lowerTerm = term.toLowerCase();
    return availableCurrencies.filter(currency => 
      currency.symbol.toLowerCase().includes(lowerTerm) || 
      currency.name.toLowerCase().includes(lowerTerm)
    );
  };

  // Handle max button click
  const handleMaxClick = () => {
    if (fromCurrency.balance) {
      setFromAmount(fromCurrency.balance.toString());
      handleFromAmountChange(fromCurrency.balance.toString());
    }
  };

  // Show confirmation modal
  const handleShowConfirmation = () => {
    // Validate input
    const amount = parseFloat(fromAmount);
    if (isNaN(amount) || amount <= 0) {
      setSwapError('Please enter a valid amount');
      return;
    }
    
    // Check balance
    if (fromCurrency.balance && amount > fromCurrency.balance) {
      setSwapError(`Insufficient ${fromCurrency.symbol} balance`);
      return;
    }
    
    // Check minimum amount
    if (amount < 0.0001) {
      setSwapError('Minimum swap amount is 0.0001');
      return;
    }
    
    setSwapError(null);
    setShowConfirmation(true);
  };

  // Execute the swap after confirmation
  const executeSwap = async () => {
    const amount = parseFloat(fromAmount);
    
    setIsSwapping(true);
    setSwapError(null);
    setSwapSuccess(null);
    
    try {
      // Call the swap function
      const success = await onSwap(fromCurrency.symbol, toCurrency.symbol, amount);
      
      if (success) {
        setSwapSuccess(`Successfully swapped ${amount} ${fromCurrency.symbol} to ${toAmount} ${toCurrency.symbol}`);
        // Reset form
        setFromAmount('');
        setToAmount('');
        
        // Refresh transactions to update the UI
        if (fetchTransactions) {
          await fetchTransactions();
        }
      } else {
        throw new Error('Swap failed. Please try again.');
      }
    } catch (error: any) {
      setSwapError(error.message || 'Swap failed. Please try again.');
    } finally {
      setIsSwapping(false);
      setShowConfirmation(false);
    }
  };

  // Calculate fees
  const calculateFee = () => {
    const amount = parseFloat(fromAmount) || 0;
    return amount * 0.001; // 0.1% fee
  };

  // Render crypto icon with fallback
  const renderCryptoIcon = (currency: CryptoCurrency) => {
    return (
      <div className="relative w-8 h-8 rounded-full overflow-hidden bg-slate-700 flex items-center justify-center">
        <img 
          src={currency.iconUrl} 
          alt={currency.symbol}
          className="w-full h-full object-contain"
          onError={(e) => {
            // On error, replace with text icon
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }}
        />
        <div className="absolute inset-0 hidden flex items-center justify-center text-white font-bold text-sm">
          {currency.symbol.substring(0, 2)}
        </div>
      </div>
    );
  };

  // Get market prices sorted by market cap
  const getSortedMarketPrices = () => {
    // Filter market data to only include allowed symbols
    const allowedSymbols = ALLOWED_SWAP_SYMBOLS.map(item => item.symbol.toUpperCase());
    
    // Filter valid market data for allowed symbols
    const validMarketData = marketData.filter(data => {
      if (!data.symbol.endsWith('USDT')) return false;
      if (data.price <= 0 || data.price === 104325.3716) return false;
      
      // Extract base symbol (remove USDT)
      const baseSymbol = data.symbol.replace('USDT', '');
      return allowedSymbols.includes(baseSymbol);
    });
    
    // Create a map of market caps from coingeckoMarketCapData
    const marketCapMap = new Map();
    
    if (coingeckoMarketCapData && coingeckoMarketCapData.length > 0) {
      coingeckoMarketCapData.forEach(coin => {
        marketCapMap.set(coin.symbol.toUpperCase(), coin.market_cap);
      });
    }
    
    // Sort by market cap (descending)
    return validMarketData.sort((a, b) => {
      // Extract base symbol (remove USDT)
      const aSymbol = a.symbol.replace('USDT', '').toLowerCase();
      const bSymbol = b.symbol.replace('USDT', '').toLowerCase();
      
      // Get market caps from map
      const aMarketCap = marketCapMap.get(aSymbol.toUpperCase()) || 0;
      const bMarketCap = marketCapMap.get(bSymbol.toUpperCase()) || 0;
      
      // Sort by market cap (descending)
      return bMarketCap - aMarketCap;
    }).slice(0, 5); // Take top 5 for display
  };

  // Filter transactions to only show swaps
  const swapTransactions = transactions.filter(tx => 
    tx.type === 'trade' && 
    tx.description.toLowerCase().includes('swap')
  );

  return (
    <div className="flex flex-col md:flex-row gap-8 p-8">
      <div className="md:w-2/3">
        {/* Swap Card */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl mb-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Swap Crypto
            </h2>
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <RefreshCw size={14} className="text-blue-400" />
              <span>Refresh Rate: 15s</span>
            </div>
          </div>

          {/* Status Messages */}
          {swapError && (
            <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
              <AlertTriangle size={20} className="text-red-400 flex-shrink-0" />
              <span className="text-red-400">{swapError}</span>
            </div>
          )}
          
          {swapSuccess && (
            <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
              <span className="text-green-400">{swapSuccess}</span>
            </div>
          )}

          {/* From Section */}
          <div className="mb-2">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-slate-400">You Pay</span>
              <span className="text-sm text-slate-400">
                Balance: {fromCurrency.balance?.toFixed(fromCurrency.symbol === 'USDT' ? 2 : 6)} {fromCurrency.symbol}
              </span>
            </div>
            <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
              <div className="flex justify-between items-center">
                <input
                  type="text"
                  value={fromAmount}
                  onChange={(e) => handleFromAmountChange(e.target.value)}
                  placeholder="0.00"
                  className="bg-transparent text-white text-3xl font-medium focus:outline-none w-full"
                />
                <div className="relative" ref={fromDropdownRef}>
                  <button
                    onClick={() => setShowFromTokens(!showFromTokens)}
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 rounded-xl px-3 py-2 transition-colors"
                  >
                    {renderCryptoIcon(fromCurrency)}
                    <span className="font-medium">{fromCurrency.symbol}</span>
                    <ChevronDown size={16} className="text-slate-400" />
                  </button>
                  
                  {showFromTokens && (
                    <div className="absolute right-0 mt-2 w-80 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
                      <div className="p-3 border-b border-slate-700">
                        <div className="relative">
                          <Search size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            placeholder="Search tokens"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-700 text-white pl-9 pr-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      
                      <div className="max-h-60 overflow-y-auto">
                        {isLoadingCurrencies ? (
                          <div className="p-4 text-center text-slate-400">
                            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                            Loading tokens...
                          </div>
                        ) : (
                          getFilteredTokens(searchTerm).map((currency) => (
                            <button
                              key={currency.symbol}
                              onClick={() => handleSelectFromCurrency(currency)}
                              className={`w-full flex items-center justify-between p-3 hover:bg-slate-700 transition-colors ${
                                currency.symbol === fromCurrency.symbol ? 'bg-slate-700' : ''
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {renderCryptoIcon(currency)}
                                <div className="text-left">
                                  <div className="font-medium text-white">{currency.name}</div>
                                  <div className="text-xs text-slate-400">{currency.symbol}</div>
                                </div>
                              </div>
                              {currency.balance !== undefined && (
                                <div className="text-right text-xs text-slate-400">
                                  {currency.balance.toFixed(currency.symbol === 'USDT' ? 2 : 6)}
                                </div>
                              )}
                            </button>
                          ))
                        )}
                        
                        {!isLoadingCurrencies && getFilteredTokens(searchTerm).length === 0 && (
                          <div className="p-4 text-center text-slate-400">
                            No tokens found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleMaxClick}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors px-2 py-1 rounded bg-blue-400/10"
                >
                  MAX
                </button>
              </div>
            </div>
          </div>

          {/* Swap Button */}
          <div className="flex justify-center -my-4 relative z-10">
            <button
              onClick={handleSwapCurrencies}
              className="w-10 h-10 bg-slate-800 hover:bg-slate-700 rounded-full flex items-center justify-center border border-slate-700 transition-all hover:scale-110"
            >
              <ArrowDown size={20} className="text-blue-400" />
            </button>
          </div>

          {/* To Section */}
          <div className="mt-2 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-slate-400">You Receive</span>
              <span className="text-sm text-slate-400">
                Balance: {toCurrency.balance?.toFixed(toCurrency.symbol === 'USDT' ? 2 : 6)} {toCurrency.symbol}
              </span>
            </div>
            <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
              <div className="flex justify-between items-center">
                <input
                  type="text"
                  value={toAmount}
                  onChange={(e) => handleToAmountChange(e.target.value)}
                  placeholder="0.00"
                  className="bg-transparent text-white text-3xl font-medium focus:outline-none w-full"
                />
                <div className="relative" ref={toDropdownRef}>
                  <button
                    onClick={() => setShowToTokens(!showToTokens)}
                    className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 rounded-xl px-3 py-2 transition-colors"
                  >
                    {renderCryptoIcon(toCurrency)}
                    <span className="font-medium">{toCurrency.symbol}</span>
                    <ChevronDown size={16} className="text-slate-400" />
                  </button>
                  
                  {showToTokens && (
                    <div className="absolute right-0 mt-2 w-80 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-50 overflow-hidden">
                      <div className="p-3 border-b border-slate-700">
                        <div className="relative">
                          <Search size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            placeholder="Search tokens"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-700 text-white pl-9 pr-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      
                      <div className="max-h-60 overflow-y-auto">
                        {isLoadingCurrencies ? (
                          <div className="p-4 text-center text-slate-400">
                            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                            Loading tokens...
                          </div>
                        ) : (
                          getFilteredTokens(searchTerm).map((currency) => (
                            <button
                              key={currency.symbol}
                              onClick={() => handleSelectToCurrency(currency)}
                              className={`w-full flex items-center justify-between p-3 hover:bg-slate-700 transition-colors ${
                                currency.symbol === toCurrency.symbol ? 'bg-slate-700' : ''
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {renderCryptoIcon(currency)}
                                <div className="text-left">
                                  <div className="font-medium text-white">{currency.name}</div>
                                  <div className="text-xs text-slate-400">{currency.symbol}</div>
                                </div>
                              </div>
                              {currency.balance !== undefined && (
                                <div className="text-right text-xs text-slate-400">
                                  {currency.balance.toFixed(currency.symbol === 'USDT' ? 2 : 6)}
                                </div>
                              )}
                            </button>
                          ))
                        )}
                        
                        {!isLoadingCurrencies && getFilteredTokens(searchTerm).length === 0 && (
                          <div className="p-4 text-center text-slate-400">
                            No tokens found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Exchange Rate */}
          <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/50 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-400">Exchange Rate</span>
              <span className="text-sm text-white">
                1 {fromCurrency.symbol} ≈ {getExchangeRate().toFixed(8)} {toCurrency.symbol}
              </span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-slate-400">Fee (0.1%)</span>
              <span className="text-sm text-white">
                {calculateFee().toFixed(6)} {fromCurrency.symbol}
              </span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-sm text-slate-400">Estimated Delivery</span>
              <span className="text-sm text-white">
                ~1-2 minutes
              </span>
            </div>
          </div>

          {/* Swap Button */}
          <button
            onClick={handleShowConfirmation}
            disabled={isSwapping || !fromAmount || parseFloat(fromAmount) <= 0}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-700 disabled:to-slate-800 text-white py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/20 flex items-center justify-center"
          >
            {isSwapping ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : !fromAmount || parseFloat(fromAmount) <= 0 ? (
              'Enter an amount'
            ) : fromCurrency.balance && parseFloat(fromAmount) > fromCurrency.balance ? (
              `Insufficient ${fromCurrency.symbol} balance`
            ) : (
              'Swap'
            )}
          </button>

          {/* Disclaimer */}
          <div className="mt-6 text-center text-xs text-slate-500">
            Swaps are executed at market price. The final rate may change due to market fluctuations.
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">About Crypto Swaps</h3>
              <p className="text-slate-300 text-sm mb-4">
                Crypto swaps allow you to exchange one cryptocurrency for another without going through the traditional order book. Swaps are executed at the current market price with minimal slippage.
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                  <div className="text-slate-400 mb-1">Advantages</div>
                  <ul className="text-slate-300 space-y-1">
                    <li>• Instant execution</li>
                    <li>• No order book waiting</li>
                    <li>• Simple interface</li>
                    <li>• Low minimum amounts</li>
                  </ul>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                  <div className="text-slate-400 mb-1">Fees</div>
                  <ul className="text-slate-300 space-y-1">
                    <li>• 0.1% trading fee</li>
                    <li>• No gas fees</li>
                    <li>• No network fees</li>
                    <li>• No hidden costs</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Market Overview & Recent Transactions */}
      <div className="md:w-1/3 space-y-8">
        {/* Market Overview */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
          <h3 className="text-xl font-semibold mb-4 text-white">Market Overview</h3>
          
          <div className="space-y-4">
            {getSortedMarketPrices().map((data, index) => {
              // Find matching token in CoinGecko data
              const symbol = data.symbol.replace('USDT', '').toLowerCase();
              const geckoToken = coingeckoMarketCapData.find(t => t.symbol.toLowerCase() === symbol);
              const iconUrl = geckoToken?.image || '';
              
              return (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-900/40 rounded-xl hover:bg-slate-900/60 transition-all duration-300">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                      {iconUrl ? (
                        <img 
                          src={iconUrl} 
                          alt={data.symbol} 
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : (
                        <span className="text-white font-bold">{data.symbol.replace('USDT', '').substring(0, 2)}</span>
                      )}
                      <span className="hidden text-white font-bold">{data.symbol.replace('USDT', '').substring(0, 2)}</span>
                    </div>
                    <div>
                      <div className="font-medium text-white">{geckoToken?.name || data.symbol.replace('USDT', '')}</div>
                      <div className="text-xs text-slate-400">{data.symbol.replace('USDT', '')}/USDT</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-white">${data.price.toFixed(2)}</div>
                    <div className={`text-xs flex items-center justify-end ${data.change_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {data.change_24h >= 0 ? <TrendingUp size={12} className="mr-1" /> : <TrendingDown size={12} className="mr-1" />}
                      {data.change_24h >= 0 ? '+' : ''}{data.change_24h.toFixed(2)}%
                    </div>
                  </div>
                </div>
              );
            })}
            
            {getSortedMarketPrices().length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <Package size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg">No market data available</p>
                <p className="text-sm text-slate-600 mt-2">Check back later for market updates</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Recent Swaps */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
          <h3 className="text-xl font-semibold mb-4 text-white">Recent Swaps</h3>
          
          {swapTransactions.length > 0 ? (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {swapTransactions.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between p-3 bg-slate-900/40 rounded-xl hover:bg-slate-900/60 transition-all duration-300">
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-slate-400" />
                    <span className="text-xs text-slate-400">{new Date(tx.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-white">{tx.description}</div>
                  <div className={`text-sm font-mono ${tx.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {tx.amount >= 0 ? '+' : ''}{tx.amount.toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500">
              <Package size={64} className="mb-6 opacity-30" />
              <p className="text-lg">No swap history found</p>
              <p className="text-sm text-slate-600 mt-2">Your swap transactions will appear here</p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-xl max-w-md w-full p-6 border border-slate-700 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Confirm Swap</h3>
              <button
                onClick={() => setShowConfirmation(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* From Currency */}
              <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
                <div className="text-sm text-slate-400 mb-2">You Pay</div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {renderCryptoIcon(fromCurrency)}
                    <div>
                      <div className="font-medium text-white">{fromCurrency.name}</div>
                      <div className="text-xs text-slate-400">{fromCurrency.symbol}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-white text-xl">{parseFloat(fromAmount).toFixed(6)}</div>
                    <div className="text-xs text-slate-400">
                      ≈ ${(parseFloat(fromAmount) * (fromCurrency.price || 0)).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Arrow */}
              <div className="flex justify-center">
                <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                  <ArrowDown size={20} className="text-blue-400" />
                </div>
              </div>
              
              {/* To Currency */}
              <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
                <div className="text-sm text-slate-400 mb-2">You Receive</div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {renderCryptoIcon(toCurrency)}
                    <div>
                      <div className="font-medium text-white">{toCurrency.name}</div>
                      <div className="text-xs text-slate-400">{toCurrency.symbol}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-white text-xl">{parseFloat(toAmount).toFixed(6)}</div>
                    <div className="text-xs text-slate-400">
                      ≈ ${(parseFloat(toAmount) * (toCurrency.price || 0)).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Details */}
              <div className="space-y-3 bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Exchange Rate</span>
                  <span className="text-white">1 {fromCurrency.symbol} ≈ {getExchangeRate().toFixed(8)} {toCurrency.symbol}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Fee (0.1%)</span>
                  <span className="text-white">{calculateFee().toFixed(6)} {fromCurrency.symbol}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Slippage Tolerance</span>
                  <span className="text-white">0.5%</span>
                </div>
              </div>
              
              {/* Warning */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-sm text-amber-400">
                Prices may change due to market fluctuations. Your transaction will be executed at the current market price.
              </div>
              
              {/* Buttons */}
              <div className="flex gap-4">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-xl font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeSwap}
                  disabled={isSwapping}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-700 disabled:to-slate-800 text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/20 flex items-center justify-center"
                >
                  {isSwapping ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    'Confirm Swap'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SwapCryptoPage;